<template>
    <div class="p-6 sm:p-8 space-y-6">
        <!-- Step 1: Install -->
        <div>
            <p
                class="text-xs font-medium uppercase tracking-wide text-taupe-500 mb-2"
            >
                Step 1 — Install
            </p>
            <div
                class="relative rounded-md bg-taupe-900 px-4 py-3 font-mono text-sm text-taupe-100"
            >
                <span class="whitespace-pre-wrap">{{ sdk.install }}</span>
                <button
                    type="button"
                    class="absolute right-3 top-2.5 text-taupe-400 hover:text-white transition-colors"
                    :title="copied === 'install' ? 'Copied!' : 'Copy'"
                    @click="$emit('copy', 'install', sdk.install)"
                >
                    <svg
                        v-if="copied !== 'install'"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
                        />
                    </svg>
                    <svg
                        v-else
                        class="h-4 w-4 text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M4.5 12.75l6 6 9-13.5"
                        />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Step 2: Initialize -->
        <div>
            <p
                class="text-xs font-medium uppercase tracking-wide text-taupe-500 mb-2"
            >
                Step 2 — Initialize
            </p>
            <div
                class="relative rounded-md bg-taupe-900 px-4 py-3 font-mono text-sm text-taupe-100 overflow-x-auto"
            >
                <pre class="whitespace-pre">{{ sdk.init }}</pre>
                <button
                    type="button"
                    class="absolute right-3 top-2.5 text-taupe-400 hover:text-white transition-colors"
                    :title="copied === 'init' ? 'Copied!' : 'Copy'"
                    @click="$emit('copy', 'init', sdk.init)"
                >
                    <svg
                        v-if="copied !== 'init'"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184"
                        />
                    </svg>
                    <svg
                        v-else
                        class="h-4 w-4 text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M4.5 12.75l6 6 9-13.5"
                        />
                    </svg>
                </button>
            </div>
        </div>

        <!-- GitHub docs link -->
        <a
            :href="sdk.readme"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 text-sm text-sky-600 hover:text-sky-800"
        >
            <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
            >
                <path
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                />
            </svg>
            View full {{ sdk.label }} SDK docs on GitHub
            <svg
                class="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                aria-hidden="true"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                />
            </svg>
        </a>
    </div>
</template>

<script setup>
defineProps({
    sdk: {
        type: Object,
        required: true,
    },
    copied: {
        type: String,
        default: null,
    },
})

defineEmits(['copy'])
</script>
